import { css, cva } from '@styled-system/css';
import {
  type ColumnDef,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, PackageOpen } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso } from 'react-virtuoso';

import { Checkbox } from './Checkbox';
import { Spinner } from './Spinner';

export type DataTableColumn<T> = ColumnDef<T, unknown>;

// Une colonne peut se déclarer « flex » : elle absorbe toute la largeur
// restante (idiome table auto : width 100% + maxWidth 0) pendant que les
// autres gardent leur taille — le tableau tient alors exactement dans sa
// card, et c'est le contenu de la colonne flex qui se tronque en ellipse.
declare module '@tanstack/react-table' {
  // Les paramètres de type sont imposés par la déclaration d'origine
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** La colonne prend la largeur restante et tronque son contenu. */
    flex?: boolean;
  }
}

/** Above this row count, the body switches to virtualized rendering. */
const VIRTUALIZATION_THRESHOLD = 50;

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  /** Stable row key getter — required for selection + React keys. */
  getRowId: (row: T) => string;
  /** Enable global text filter (matches against any string column). */
  globalFilter?: string;
  /** Multi-select with checkbox column at the start. */
  selection?: {
    selected: string[];
    onChange: (selected: string[]) => void;
  };
  loading?: boolean;
  /** Empty state — shown when data is empty after filtering. */
  emptyTitle?: ReactNode;
  emptyHint?: ReactNode;
  emptyIcon?: ReactNode;
  /** Click on a row (ignored when click target is interactive). */
  onRowClick?: (row: T) => void;
  className?: string;
}

const shell = css({
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  bg: 'surface.default',
  overflow: 'hidden',
  // Écrans étroits : défilement horizontal plutôt que colonnes coupées
  overflowX: 'auto',
});

const tableEl = css({
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: 'sans',
  fontSize: 'bodySm',
});

const headRow = css({
  bg: 'surface.subtle',
});

const headCell = cva({
  base: {
    height: '40px',
    paddingY: 0,
    paddingX: '6',
    fontSize: 'micro',
    fontWeight: 'medium',
    color: 'text.tertiary',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    textAlign: 'left',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'border.default',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    bg: 'surface.subtle',
    boxSizing: 'border-box',
  },
  variants: {
    sortable: {
      true: {
        cursor: 'pointer',
        _hover: { color: 'text.secondary' },
      },
      false: {},
    },
  },
});

const headInner = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
});

const sortIcon = css({
  color: 'text.tertiary',
  flexShrink: 0,
});

const sortIconActive = css({
  color: 'text.primary',
});

const bodyRow = cva({
  base: {
    transition: 'background 120ms ease',
    _hover: { bg: 'surface.subtle' },
  },
  variants: {
    selected: {
      true: { bg: 'brand.primarySoft', _hover: { bg: 'brand.primarySoft' } },
      false: {},
    },
    clickable: {
      true: { cursor: 'pointer' },
      false: {},
    },
  },
});

const bodyCell = css({
  paddingY: '5',
  paddingX: '6',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  verticalAlign: 'middle',
  color: 'text.primary',
});

const selectColumn = css({
  width: '40px',
  paddingX: '4',
});

const empty = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5',
  paddingY: '20',
  paddingX: '8',
  color: 'text.tertiary',
});

const emptyTitleStyle = css({ fontSize: 'h3', fontWeight: 'semibold', color: 'text.secondary' });
const emptyHintStyle = css({ fontSize: 'bodySm', color: 'text.tertiary' });

const loadingOverlay = css({
  position: 'absolute',
  inset: 0,
  bg: 'surface.default',
  opacity: 0.7,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
});

const loadingInline = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingY: '20',
  paddingX: '8',
  minHeight: '320px',
});

const findScrollAncestor = (el: HTMLElement | null): HTMLElement | null => {
  let cur: HTMLElement | null = el?.parentElement ?? null;
  while (cur) {
    if (cur.dataset.scrollContainer !== undefined) return cur;
    const style = getComputedStyle(cur);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') return cur;
    cur = cur.parentElement;
  }
  return null;
};

export function DataTable<T>({
  data,
  columns,
  getRowId,
  globalFilter,
  selection,
  loading,
  emptyTitle,
  emptyHint,
  emptyIcon,
  onRowClick,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setScrollParent(findScrollAncestor(wrapperRef.current));
  }, []);

  const rowSelection: RowSelectionState = useMemo(() => {
    if (!selection) return {};
    return Object.fromEntries(selection.selected.map((id) => [id, true]));
  }, [selection]);

  const tableColumns = useMemo<DataTableColumn<T>[]>(() => {
    if (!selection) return columns;
    const selectColumnDef: DataTableColumn<T> = {
      id: '__select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={() => table.toggleAllRowsSelected()}
        />
      ),
      cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onChange={() => row.toggleSelected()} />,
      enableSorting: false,
    };
    return [selectColumnDef, ...columns];
  }, [columns, selection]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      rowSelection,
      globalFilter: globalFilter ?? '',
    },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      if (!selection) return;
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      selection.onChange(Object.keys(next).filter((id) => next[id]));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId,
    enableRowSelection: !!selection,
    enableGlobalFilter: globalFilter !== undefined,
  });

  const visibleRows = table.getRowModel().rows;
  const showEmpty = !loading && visibleRows.length === 0;
  const useVirtualization = scrollParent !== null && visibleRows.length > VIRTUALIZATION_THRESHOLD;

  const renderHeaderRow = (key: string | number) => (
    <tr key={key} className={headRow}>
      {table.getHeaderGroups()[0]!.headers.map((header) => {
        const sortable = header.column.getCanSort();
        const sortDir = header.column.getIsSorted();
        const isSelectColumn = header.column.id === '__select';
        return (
          <th
            key={header.id}
            className={`${headCell({ sortable })} ${isSelectColumn ? selectColumn : ''}`}
            style={
              header.column.columnDef.meta?.flex
                ? { width: '100%', maxWidth: 0 }
                : { width: header.getSize() === 150 ? undefined : header.getSize() }
            }
            onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
          >
            <span className={headInner}>
              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              {sortable && (
                <span className={`${sortIcon} ${sortDir ? sortIconActive : ''}`}>
                  {sortDir === 'asc' ? (
                    <ArrowUp size={12} strokeWidth={2} />
                  ) : sortDir === 'desc' ? (
                    <ArrowDown size={12} strokeWidth={2} />
                  ) : (
                    <ArrowUpDown size={12} strokeWidth={1.75} />
                  )}
                </span>
              )}
            </span>
          </th>
        );
      })}
    </tr>
  );

  const renderCells = (row: Row<T>) =>
    row.getVisibleCells().map((cell) => {
      const isSelectColumn = cell.column.id === '__select';
      return (
        <td
          key={cell.id}
          className={`${bodyCell} ${isSelectColumn ? selectColumn : ''}`}
          style={
            cell.column.columnDef.meta?.flex
              ? { width: '100%', maxWidth: 0 }
              : { width: cell.column.getSize() === 150 ? undefined : cell.column.getSize() }
          }
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      );
    });

  const handleRowClick = onRowClick
    ? (row: Row<T>) => (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('button, a, input, label, [role="button"], [data-no-row-click]')) return;
        onRowClick(row.original);
      }
    : undefined;

  return (
    <div ref={wrapperRef} className={`${shell} ${className ?? ''}`} style={{ position: 'relative' }}>
      {useVirtualization ? (
        <TableVirtuoso<Row<T>>
          customScrollParent={scrollParent ?? undefined}
          data={visibleRows}
          components={{
            Table: (props) => <table {...props} className={tableEl} />,
            TableRow: ({ item: row, ...props }) => (
              <tr
                {...props}
                className={bodyRow({ selected: row.getIsSelected(), clickable: !!onRowClick })}
                onClick={handleRowClick ? handleRowClick(row) : undefined}
              />
            ),
          }}
          fixedHeaderContent={() => renderHeaderRow('header')}
          itemContent={(_index, row) => renderCells(row)}
        />
      ) : (
        <table className={tableEl}>
          <thead>{renderHeaderRow('static-header')}</thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.id}
                className={bodyRow({ selected: row.getIsSelected(), clickable: !!onRowClick })}
                onClick={handleRowClick ? handleRowClick(row) : undefined}
              >
                {renderCells(row)}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showEmpty && (
        <div className={empty}>
          {emptyIcon ?? <PackageOpen size={48} strokeWidth={1.5} />}
          <div className={emptyTitleStyle}>{emptyTitle ?? t('ui.emptyTitle')}</div>
          {emptyHint && <div className={emptyHintStyle}>{emptyHint}</div>}
        </div>
      )}

      {loading && visibleRows.length === 0 && (
        <div className={loadingInline}>
          <Spinner size={32} />
        </div>
      )}

      {loading && visibleRows.length > 0 && (
        <div className={loadingOverlay}>
          <Spinner size={32} />
        </div>
      )}
    </div>
  );
}
